import {
  IMPACT_SERIES,
  Initiative,
  InitiativeImpactSeries,
  MonthlyImpactMap,
} from "aint-types";
import { formatDate, MONTH_TO_NUMBER } from "aint-utils";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(timezone);
dayjs.extend(isBetween);

export function removeUnusedFieldsFromEditInitiative(initiative: Initiative) {
  if (initiative.itChangeRequired !== true) {
    initiative.itProjectId = "";
  }

  if (initiative.oeProgram !== true) {
    initiative.oeProgramNr = "";
  }

  if (initiative.primaryLineOfBusiness !== "Other") {
    delete initiative.primaryLineOfBusinessOther;
  }

  if (initiative.primaryLineOfBusiness !== "Commercial") {
    delete initiative.commercialLineOfBusiness;
  }

  if (
    initiative.secondaryLineOfBusiness &&
    !initiative.secondaryLineOfBusiness.includes("Other")
  ) {
    delete initiative.secondaryLineOfBusinessOther;
  }

  if (initiative.implementationProgram !== "Other") {
    delete initiative.implementationProgramOther;
  }

  if (
    initiative.initiativeEnablers &&
    !initiative.initiativeEnablers.includes("Automation")
  ) {
    delete initiative.automationAIPowered;
    delete initiative.automationGenAIPowered;
    delete initiative.automationComplexity;
    delete initiative.automationInvolvedSystem;
    delete initiative.automationMethodology;
    delete initiative.automationSolutionImplementationProvider;
    delete initiative.automationTechnicalSolution;
    delete initiative.automationUnderlyingPlatform;
  }
  if (
    initiative.initiativeEnablers &&
    !initiative.initiativeEnablers.includes("Process Mining")
  ) {
    delete initiative.processMiningEnablementType;
    delete initiative.processMiningEnablementTypeOther;
  }

  if (
    initiative.processMiningEnablementType &&
    !initiative.processMiningEnablementType.includes("Other")
  ) {
    delete initiative.processMiningEnablementTypeOther;
  }

  if (
    initiative.primaryImpactDriver &&
    !initiative.primaryImpactDriver.includes("Qualitative")
  ) {
    delete initiative.qualitativeImpactType;
    delete initiative.qualitativeImpactTypeOther;
  }

  if (initiative.qualitativeImpactType === "Other") {
    delete initiative.qualitativeImpactTypeOther;
  }

  if (!initiative.tieServiceExpensesIncludesHrCosts) {
    delete initiative["hrCostImpactActual"];
    delete initiative["hrCostImpactPlanned"];
  }

  const startDate = dayjs(initiative.startDate).startOf("month");
  const endDate = dayjs(initiative.endDate).endOf("month");
  const monitoringEndDate = dayjs(initiative.monitoringEndDate).endOf("month");

  // Set the dates back to the initiative to be saved. This is to correct any "mid of the month" dates that might have been set via strapi.
  initiative.startDate = formatDate(startDate.toDate());
  initiative.endDate = formatDate(endDate.toDate());
  initiative.monitoringEndDate = formatDate(monitoringEndDate.toDate());

  for (const seriesType of IMPACT_SERIES) {
    if (!(initiative.applicableImpactSeries ?? []).includes(seriesType)) {
      delete initiative[
        (seriesType + "Actual") as keyof InitiativeImpactSeries
      ];
      delete initiative[
        (seriesType + "Planned") as keyof InitiativeImpactSeries
      ];
    }

    ["Actual", "Planned"].forEach((actualOrPlanned) => {
      const series = initiative[
        (seriesType + actualOrPlanned) as keyof InitiativeImpactSeries
      ] as MonthlyImpactMap;

      if (!series) {
        return;
      }

      for (const [year, impactByMonth] of Object.entries(series)) {
        const yearNumber = +year;
        if (
          startDate.year() < yearNumber &&
          yearNumber > monitoringEndDate.year()
        ) {
          continue;
        }

        for (const [month, impact] of Object.entries(impactByMonth)) {
          const monthNumber = MONTH_TO_NUMBER[month];

          const impactDate = dayjs(`${year}-${monthNumber + 1}-15`);

          if (
            !impact ||
            impact.length === 0 ||
            !impactDate.isBetween(startDate, monitoringEndDate)
          ) {
            delete series[year][month];
          }
        }
      }
    });
  }

  return initiative;
}
